import { type ReactElement, type ReactNode, useEffect, useId, useRef } from 'react'
import { FocusScope } from 'react-aria'
import { Button, Modal, ModalOverlay } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Built-in strings of {@link Dialog}, overridable per locale (tech §59).
 *
 * @property close - Accessible name of the close control. Default `Close`.
 */
export interface DialogLabels {
	close: string
}

const DEFAULT_LABELS: DialogLabels = { close: 'Close' }

/**
 * Props for the {@link Dialog} atom.
 *
 * @property isOpen - Controlled open state. The dialog never closes itself.
 * @property onOpenChange - Reports a close request (`false`); the consumer decides.
 * @property title - Accessible title rendered in the dialog header.
 * @property children - Dialog body.
 * @property className - Extra Tailwind classes merged onto the dialog panel.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false)
 *
 * <Dialog isOpen={isOpen} onOpenChange={setIsOpen} title="Confirm">
 *   Are you sure?
 * </Dialog>
 * ```
 */
export interface DialogProps {
	isOpen: boolean
	onOpenChange: (isOpen: boolean) => void
	title: string
	children: ReactNode
	className?: string
	labels?: Partial<DialogLabels>
}

/**
 * Accessible modal dialog primitive.
 *
 * @remarks
 * - **Accessibility:** `role="dialog"` with `aria-modal="true"`, labelled by its
 *   own heading. React Aria's `<ModalOverlay>`/`<Modal>` supply the focus trap,
 *   scroll lock, outside-click and Escape dismissal.
 * - **Why the dialog element is rendered here** rather than via React Aria's
 *   `<Dialog>`: that component filters `aria-modal` out of the DOM props (it
 *   relies on `aria-hidden` siblings instead). Rendering the element directly
 *   keeps the explicit `aria-modal="true"` the design system asserts, while the
 *   surrounding `<Modal>` still provides every behavioural guarantee.
 * - **Controlled:** rendering is driven entirely by `isOpen`. Escape and the
 *   close control report `onOpenChange(false)`; the dialog does **not** close
 *   itself, so the consumer owns the open state (ADR-0031 decoupling seam).
 * - **Focus containment:** a `FocusScope contain` keeps Tab / Shift+Tab inside
 *   the panel, as `aria-modal="true"` promises. React Aria's `<Dialog>` would
 *   contain focus itself; this element replaces it (see above), so it is
 *   restored explicitly (TO-420).
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @example
 * ```tsx
 * import { Dialog } from '@today/ui-primitives'
 *
 * function DeleteConfirmation() {
 *   const [isOpen, setIsOpen] = useState(false)
 *   return (
 *     <Dialog isOpen={isOpen} onOpenChange={setIsOpen} title="Delete this item?">
 *       <p>This cannot be undone.</p>
 *     </Dialog>
 *   )
 * }
 * ```
 *
 * @see packages/ui-primitives/src/dialog/dialog.stories.tsx for visual examples
 */
export function Dialog({ isOpen, onOpenChange, title, children, className, labels }: DialogProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	const titleId = useId()
	const panelRef = useRef<HTMLDivElement>(null)

	// Move focus into the panel on open. React Aria's <Modal> restores focus on
	// close and <FocusScope> contains it, but the initial focus move is <Dialog>'s job — which this
	// component replaces (see above). Without it nothing inside is focused, so
	// Escape would never reach the overlay's key handler.
	useEffect(() => {
		if (isOpen) panelRef.current?.focus()
	}, [isOpen])

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			isDismissable
			className="fixed inset-0 z-50 flex items-center justify-center bg-ds-text/20 p-4"
		>
			<Modal className="w-full max-w-md">
				<FocusScope contain>
					<div
						ref={panelRef}
						role="dialog"
						aria-modal="true"
						aria-labelledby={titleId}
						tabIndex={-1}
						className={cn(
							'flex flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6 shadow-lg outline-none',
							className,
						)}
					>
						<header className="flex items-start justify-between gap-4">
							<h2 id={titleId} className="text-ds-lg font-semibold text-ds-text">
								{title}
							</h2>
							<Button
								aria-label={text.close}
								onPress={() => onOpenChange(false)}
								className="rounded-ds-sm px-2 text-ds-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
							>
								✕
							</Button>
						</header>
						<div className="text-ds-base text-ds-text">{children}</div>
					</div>
				</FocusScope>
			</Modal>
		</ModalOverlay>
	)
}
